import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { 
  FileText, 
  Plus, 
  Trash2, 
  Clock, 
  Save, 
  Send, 
  AlertTriangle, 
  CheckCircle, 
  ChevronLeft 
} from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useApi } from '../../hooks/useApi';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';

interface Mission {
  _id: string;
  missionNumber: string;
  client: { companyName?: string; firstName?: string; lastName?: string; };
  technician: { firstName: string; lastName: string; };
  serviceType: { name: string; };
}

interface Equipment {
  _id: string;
  name: string;
}

import ImageUploader from '../../components/ImageUploader';

interface ReportFormData {
  mission: string;
  reportDate: string;
  startTime: string;
  endTime: string;
  duration: number;
  photos: { file?: File | null; url?: string }[]; // Updated to accept File | null
  anomalies: { description: string; isCritical: boolean }[];
  isBatutalCompliant: boolean;
  notes: string;
  certificateNumber?: string;
  serviceType: string; // Changed from type
  subServiceType: string; // Added
  location: {
    address: string;
    city: string;
    postalCode: string;
  };
  selectedEquipment: string;
  selectedProducts: string;
  selectedKits: string; // New field for selected kits
  workPerformed: string;
}

interface Product {
  _id: string;
  name: string;
}

interface Kit {
  _id: string;
  name: string;
}

interface SubServiceType {
  _id: string;
  name: string;
  description?: string;
}

interface ServiceType {
  _id: string;
  name: string;
  category: string;
  subTypes: SubServiceType[];
}

const AddReport: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  
  const navigate = useNavigate();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [kits, setKits] = useState<Kit[]>([]); // New kits state
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]); // New serviceTypes state
  const isEditing = !!reportId;
  const isViewMode = window.location.pathname.includes('/view/');

  const { 
    register, 
    handleSubmit, 
    control, 
    watch, 
    setValue, 
    reset, 
    formState: { errors } 
  } = useForm<ReportFormData>({
    defaultValues: {
      photos: [],
      anomalies: [],
      isBatutalCompliant: true,
      selectedKits: '', // Initialize selectedKits
    }
  });

  const { fields: photoFields, append: appendPhoto, remove: removePhoto } = useFieldArray({ control, name: "photos" });
  const { fields: anomalyFields, append: appendAnomaly, remove: removeAnomaly } = useFieldArray({ control, name: "anomalies" });

  const [reportData, setReportData] = useState<any>(null);
  const { execute: fetchReport } = useApi<any, [string]>(apiService.reports.getById);
  const { execute: createReport } = useApi<any, [FormData]>(apiService.reports.create);
  const { execute: updateReport } = useApi<any, [string, FormData]>(apiService.reports.update);

  useEffect(() => {
    const fetchMissions = async () => {
      try {
        const response = await apiService.missions.getMyMissions({ limit: 1000 }); // Fetch all missions for tech
        setMissions(response.items || []);
      } catch (error) {
        toast.error('Erreur lors du chargement des missions.');
      }
    };
    fetchMissions();
  }, []);

  useEffect(() => {
    const fetchEquipments = async () => {
      try {
        const response = await apiService.equipments.getAll({});
        setEquipments(response.items || []);
      } catch (error) {
        toast.error('Erreur lors du chargement des équipements.');
      }
    };
    fetchEquipments();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await apiService.products.getAll({});
        setProducts(response.items || []);
      } catch (error) {
        toast.error('Erreur lors du chargement des produits.');
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => { // New useEffect for Kits
    const fetchKits = async () => {
      try {
        const response = await apiService.kits.getAll({});
        setKits(response.items || []);
      } catch (error) {
        toast.error('Erreur lors du chargement des kits.');
      }
    };
    fetchKits();
  }, []);

  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const response = await apiService.serviceTypes.getAll({});
        setServiceTypes(response.items || []);
      } catch (error) {
        toast.error('Erreur lors du chargement des types de service.');
      }
    };
    fetchServiceTypes();
  }, []);

  useEffect(() => {
    if (reportId) {
      const loadReport = async () => {
        const data = await fetchReport(reportId);
        if (data) {
          setReportData(data);
          reset({
            ...data,
            mission: data.mission?._id || '', // Map mission to its ID
            reportDate: data.date ? new Date(data.date).toISOString().split('T')[0] : '', // Use data.date
            serviceType: data.serviceType?._id || '', // Map serviceType to its ID
            subServiceType: data.subServiceType || '', // Map subServiceType
            isBatutalCompliant: data.batutalCompliant || false, // Use data.batutalCompliant
            selectedEquipment: data.selectedEquipment?._id || '',
            selectedProducts: data.selectedProducts?._id || '',
            selectedKits: data.selectedKits?._id || '',
            photos: (data.photos || []).map((photo: any) => ({ file: undefined, url: photo.url })), // Correct photo mapping
            anomalies: data.anomalies || [],
          });
        }
      };
      loadReport();
    }
  }, [reportId, fetchReport, reset]);

  const startTime = watch('startTime');
  const endTime = watch('endTime');

  useEffect(() => {
    if (startTime && endTime) {
      const start = new Date(`1970-01-01T${startTime}`);
      const end = new Date(`1970-01-01T${endTime}`);
      const diff = (end.getTime() - start.getTime()) / (1000 * 60); // difference in minutes
      setValue('duration', diff > 0 ? diff : 0);
    }
  }, [startTime, endTime, setValue]);

  const onSubmit = async (data: ReportFormData, status: 'draft' | 'completed') => {
    console.log('Frontend - Data before FormData construction:', data);
    const formData = new FormData();

    // Append simple fields
    formData.append('mission', data.mission);
    formData.append('reportDate', data.reportDate);
    formData.append('startTime', data.startTime);
    formData.append('endTime', data.endTime);
    formData.append('duration', String(data.duration));
    formData.append('isBatutalCompliant', String(data.isBatutalCompliant));
    formData.append('notes', data.notes);
    if (data.certificateNumber) {
      formData.append('certificateNumber', data.certificateNumber);
    }
    formData.append('serviceType', data.serviceType);
    formData.append('subServiceType', data.subServiceType);

    // Append selected equipment
    if (data.selectedEquipment) {
      formData.append('selectedEquipment', data.selectedEquipment);
    }

    // Append selected products
    if (data.selectedProducts) {
      formData.append('selectedProducts', data.selectedProducts);
    }

    // Append selected kits
    if (data.selectedKits) {
      formData.append('selectedKits', data.selectedKits);
    }

    formData.append('workPerformed', data.workPerformed);

    // Append nested location object as a JSON string
    formData.append('location', JSON.stringify(data.location));

    // Append anomalies array as a JSON string
    formData.append('anomalies', JSON.stringify(data.anomalies));

    // Append photos
    data.photos.forEach((photo, index) => {
      if (photo.file) { // Check if photo.file is a File object
        formData.append('photos', photo.file);
      } else if (photo.url) {
        formData.append(`existingPhotos[${index}]`, photo.url);
      }
    });

    formData.append('status', status);

    // Log FormData content
    console.log('Frontend - FormData content:');
    for (let pair of formData.entries()) {
        console.log(pair[0]+ ': ' + pair[1]);
    }

    try {
      if (isEditing) {
        await updateReport(reportId, formData);
      } else {
        await createReport(formData);
      }
      navigate('/tech/reports');
    } catch (error) {
      // Error is already handled by useCrudApi hook
    }
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-4xl mx-auto pb-24">
        <Button variant="ghost" onClick={() => navigate('/tech/reports')} className="flex items-center">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Retour aux rapports
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <FileText className="w-8 h-8 mr-3 text-primary-600" />
          {isViewMode ? 'Détails du Rapport Technique' : (isEditing ? 'Modifier le Rapport Technique' : 'Nouveau Rapport Technique')}
        </h1>

        <form className="space-y-8">
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Informations Générales</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mission</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.mission?.missionNumber} - {reportData?.mission?.client?.companyName || (reportData?.mission?.client ? `${reportData.mission.client.firstName} ${reportData.mission.client.lastName}` : 'Client Inconnu')} - {reportData?.mission?.serviceType?.name}</p>
                ) : (
                  <select {...register('mission', { required: 'La mission est requise' })} className="w-full border border-gray-300 rounded-lg px-3 py-2">
                    <option value="">Sélectionner une mission</option>
                    {missions.map(mission => (
                      <option key={mission._id} value={mission._id}>
                        {mission.missionNumber} - {mission.client ? (mission.client.companyName || `${mission.client.firstName} ${mission.client.lastName}`) : 'Client Inconnu'} ({mission.serviceType ? mission.serviceType.name : 'Service Inconnu'})
                      </option>
                    ))}
                  </select>
                )}
                {errors.mission && <p className="text-red-500 text-sm mt-1">{errors.mission.message}</p>}
              </div>
              <div>                <label className="block text-sm font-medium text-gray-700 mb-2">Date du rapport</label>                {isViewMode ? (                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{new Date(reportData?.reportDate || '').toLocaleDateString('fr-FR')}</p>                ) : (                  <input type="date" {...register('reportDate', { required: 'La date est requise' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />                )}                {errors.reportDate && <p className="text-red-500 text-sm mt-1">{errors.reportDate.message}</p>}              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de Service</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.serviceType?.name}</p>
                ) : (
                  <select
                    {...register('serviceType', { required: 'Le type de service est requis' })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Sélectionner un type de service</option>
                    {serviceTypes.map(st => (
                      <option key={st._id} value={st._id}>{st.name}</option>
                    ))}
                  </select>
                )}
                {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sous-Type de Service</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.subServiceType || 'N/A'}</p>
                ) : (
                  <select
                    {...register('subServiceType')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    disabled={!watch('serviceType')} // Disable if no service type is selected
                  >
                    <option value="">Sélectionner un sous-type</option>
                    {watch('serviceType') && serviceTypes.find(st => st._id === watch('serviceType'))?.subTypes.map(sst => (
                      <option key={sst._id} value={sst.name}>{sst.name}</option>
                    ))}
                  </select>
                )}
                {errors.subServiceType && <p className="text-red-500 text-sm mt-1">{errors.subServiceType.message}</p>}
              </div>              
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Détails de l'intervention</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Adresse</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.location?.address}</p>
                ) : (
                  <input type="text" {...register('location.address', { required: 'L\'adresse est requise' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                )}
                {errors.location?.address && <p className="text-red-500 text-sm mt-1">{errors.location.address.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.location?.city}</p>
                ) : (
                  <input type="text" {...register('location.city', { required: 'La ville est requise' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                )}
                {errors.location?.city && <p className="text-red-500 text-sm mt-1">{errors.location.city.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Code Postal</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.location?.postalCode}</p>
                ) : (
                  <input type="text" {...register('location.postalCode', { required: 'Le code postal est requis' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                )}
                {errors.location?.postalCode && <p className="text-red-500 text-sm mt-1">{errors.location.postalCode.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Équipement(s) utilisé(s)</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">
                    {reportData?.selectedEquipment?.name || 'N/A'}
                  </p>
                ) : (
                  <select
                    {...register('selectedEquipment', { required: 'Au moins un équipement est requis' })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {equipments.map(eq => (
                      <option key={eq._id} value={eq._id}>{eq.name}</option>
                    ))}
                  </select>
                )}
                {errors.selectedEquipment && <p className="text-red-500 text-sm mt-1">{errors.selectedEquipment.message}</p>}
              </div>
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Produit(s) utilisé(s)</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">
                    {reportData?.selectedProducts?.name || 'N/A'}
                  </p>
                ) : (
                  <select
                    {...register('selectedProducts')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {products.map(prod => (
                      <option key={prod._id} value={prod._id}>{prod.name}</option>
                    ))}
                  </select>
                )}
                {errors.selectedProducts && <p className="text-red-500 text-sm mt-1">{errors.selectedProducts.message}</p>}
              </div>
              <div> {/* New Kits selection */}
                <label className="block text-sm font-medium text-gray-700 mb-2">Kit(s) utilisé(s)</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">
                    {reportData?.selectedKits?.name || 'N/A'}
                  </p>
                ) : (
                  <select
                    {...register('selectedKits')}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    {kits.map(kit => (
                      <option key={kit._id} value={kit._id}>{kit.name}</option>
                    ))}
                  </select>
                )}
                {errors.selectedKits && <p className="text-red-500 text-sm mt-1">{errors.selectedKits.message}</p>}
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Travaux effectués</label>
              {isViewMode ? (
                <p className="text-gray-900 p-2 bg-gray-100 rounded-lg whitespace-pre-wrap">{reportData?.workPerformed}</p>
              ) : (
                <textarea {...register('workPerformed', { required: 'La description des travaux est requise' })} rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2"></textarea>
              )}
              {errors.workPerformed && <p className="text-red-500 text-sm mt-1">{errors.workPerformed.message}</p>}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Durée de l'intervention</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Heure de début</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.startTime}</p>
                ) : (
                  <input type="time" {...register('startTime', { required: 'Heure de début requise' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Heure de fin</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.endTime}</p>
                ) : (
                  <input type="time" {...register('endTime', { required: 'Heure de fin requise' })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Durée</label>
                <div className="flex items-center h-10 px-3 bg-gray-100 rounded-lg">
                  <Clock className="w-4 h-4 mr-2 text-gray-500" />
                  <span>{Math.floor(watch('duration') / 60) || 0}h {watch('duration') % 60 || 0}min</span>
                </div>
              </div>
            </div>
          </Card>

          {/* --- Photos de l'intervention --- */}
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Photos de l'intervention</h2>

            {/* Bouton d'ajout d'image */}
            {!isViewMode && photoFields.length < 10 && (
              <div className="mb-4">
                <label className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg cursor-pointer hover:bg-primary-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter une image ({photoFields.length} / 10)
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        appendPhoto({ file, url: undefined });
                      }
                      // Reset file input to allow selecting the same file again
                      e.target.value = '';
                    }}
                  />
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Vous pouvez ajouter jusqu’à 10 images. Max 10 Mo par image.
                </p>
              </div>
            )}

            {/* Grille de vignettes */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {photoFields.map((field, idx) => {
                const fileObj = watch(`photos.${idx}.file`);
                const url = watch(`photos.${idx}.url`);
                const src = fileObj ? URL.createObjectURL(fileObj as File) : url;

                return (
                  <div key={field.id} className="relative group">
                    {src ? (
                      src.endsWith('.pdf') ? (
                        <a href={src} target="_blank" rel="noopener noreferrer" className="w-full h-40 flex items-center justify-center bg-gray-100 rounded-lg border text-primary-600 hover:underline">
                          <FileText className="w-10 h-10" />
                          <span className="ml-2">Voir PDF</span>
                        </a>
                      ) : (
                        <img
                          src={src}
                          alt={`photo-${idx}`}
                          className="w-full h-40 object-cover rounded-lg border"
                        />
                      )
                    ) : (
                      <div className="w-full h-40 bg-gray-100 rounded-lg border grid place-items-center text-gray-400">
                        Pas d’aperçu
                      </div>
                    )}

                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 text-xs font-medium px-2 py-1 rounded-md shadow"
                      >
                        Supprimer
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Anomalies et remarques</h2>
            <div className="space-y-4">
              {isViewMode ? (
                reportData?.anomalies && reportData.anomalies.length > 0 ? (
                  reportData.anomalies.map((anomaly: { description: string; isCritical: boolean }, index: number) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-900">{anomaly.description}</p>
                      {anomaly.isCritical && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Critique
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500">Aucune anomalie ou remarque.</p>
                )
              ) : (
                anomalyFields.map((field, index) => (
                  <div key={field.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                    <div className="flex-grow">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description de l'anomalie</label>
                      <textarea {...register(`anomalies.${index}.description`, { required: true })} rows={2} className="w-full border border-gray-300 rounded-lg px-3 py-2"></textarea>
                    </div>
                    <div className="flex-shrink-0">
                      <label className="flex items-center space-x-2 mt-2">
                        <input type="checkbox" {...register(`anomalies.${index}.isCritical`)} className="rounded" />
                        <span className="text-sm font-medium">Critique</span>
                      </label>
                    </div>
                    <Button type="button" variant="ghost" onClick={() => removeAnomaly(index)}><Trash2 className="w-5 h-5 text-red-500" /></Button>
                  </div>
                ))
              )}
            </div>
            {!isViewMode && (
              <Button type="button" onClick={() => appendAnomaly({ description: '', isCritical: false })} className="mt-4 flex items-center">
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une anomalie
              </Button>
            )}
          </Card>

          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Conformité et Notes</h2>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {isViewMode ? (
                  reportData?.isBatutalCompliant ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Conforme BATUTA
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      Non conforme BATUTA
                    </span>
                  )
                ) : (
                  <>
                    <input type="checkbox" {...register('isBatutalCompliant')} id="isBatutalCompliant" className="h-4 w-4 rounded" />
                    <label htmlFor="isBatutalCompliant" className="font-medium text-gray-700">Conforme BATUTA</label>
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de certificat (si applicable)</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg">{reportData?.certificateNumber || 'N/A'}</p>
                ) : (
                  <input type="text" {...register('certificateNumber')} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes générales</label>
                {isViewMode ? (
                  <p className="text-gray-900 p-2 bg-gray-100 rounded-lg whitespace-pre-wrap">{reportData?.notes || 'Aucune note.'}</p>
                ) : (
                  <textarea {...register('notes')} rows={4} className="w-full border border-gray-300 rounded-lg px-3 py-2"></textarea>
                )}
              </div>
            </div>
          </Card>

          {!isViewMode && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-10">
              <div className="max-w-4xl mx-auto flex justify-end space-x-4">
                <Button type="button" onClick={handleSubmit(d => onSubmit(d as ReportFormData, 'draft'))} variant="secondary" className="flex items-center">
                  <Save className="w-5 h-5 mr-2" />
                  Enregistrer comme brouillon
                </Button>
                <Button type="button" onClick={handleSubmit(d => onSubmit(d as ReportFormData, 'completed'))} className="flex items-center">
                  <Send className="w-5 h-5 mr-2" />
                  Finaliser et envoyer
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </Layout>
  );
};

export default AddReport;
